import React, { useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import RecentActivity from "./RecentActivity";

const LeadAnalyticsModal = ({ isOpen, onClose, leadData }) => {
  const closeRef = useRef(null);

  // Calculate analytics from activities data
  const getAnalyticsStats = () => {
    const activities = leadData?.activities || [];
    
    const totalActivities = activities.length;
    const stageChanges = activities.filter(a => a.ActivityType?.toLowerCase().includes('stage')).length;
    const emailActivities = activities.filter(a => a.ActivityType?.toLowerCase().includes('email')).length;
    const callActivities = activities.filter(a => a.ActivityType?.toLowerCase().includes('call') || a.ActivityType?.toLowerCase().includes('phone')).length;
    
    return {
      totalActivities,
      stageChanges,
      emailActivities,
      callActivities
    };
  };

  const { totalActivities, stageChanges, emailActivities, callActivities } = getAnalyticsStats();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[99998] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
        initialFocus={closeRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <ChartBarIcon className="h-5 w-5 text-blue-600" />
                Lead Analytics
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Analytics and insights for {leadData?.name || "this lead"}.
              </p>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {leadData?.leadScore === 'Unscored' ? '--' : (leadData?.leadScore || 0)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Lead Score
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {totalActivities}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Total Activities
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {stageChanges}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Stage Changes
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {emailActivities + callActivities}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Communications
                    </div>
                  </div>
                </div>
                
                {/* Recent Activity Section - now using real data */}
                <RecentActivity activities={leadData?.activities || []} />
              </div>
              
              <div className="mt-6 text-end">
                <Button
                  onClick={onClose}
                  ref={closeRef}
                  className="min-w-[7rem] rounded-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default LeadAnalyticsModal;
